import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BannerLivePreview } from '../components/BannerLivePreview';
import { api, type PublicInviteInfo } from '../services/api';
import { bannerCssVars, parseBannerOptionsFromSearch } from '../utils/bannerDisplay';
import { normalizeInviteCode } from '../utils/inviteLink';

/** Fallback if public invite API is unreachable — keep OBS usable. */
const DEFAULT_BONUS_DAYS = 30;

export function StreamerBannerPage() {
  const { code: rawCode } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const code = normalizeInviteCode(rawCode) ?? '';
  const opts = useMemo(
    () => parseBannerOptionsFromSearch(searchParams.toString()),
    [searchParams],
  );

  const [info, setInfo] = useState<PublicInviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const cssVars = useMemo(() => bannerCssVars(opts), [opts]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add('banner-overlay');
    body.classList.add('banner-page-body');
    document.getElementById('boot-splash')?.remove();
    return () => {
      html.classList.remove('banner-overlay');
      body.classList.remove('banner-page-body');
    };
  }, []);

  useEffect(() => {
    if (!code) {
      setError('Код не указан');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await api.getPublicInvite(code);
      if (cancelled) return;
      if (res.data) {
        setInfo(res.data);
        setError(null);
      } else {
        setInfo({
          code,
          bonus_days: DEFAULT_BONUS_DAYS,
          subscription_tier: 'premium',
          invite_url: '',
        });
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) {
    return (
      <div className="streamer-banner-root" style={cssVars as CSSProperties}>
        <div className="streamer-banner is-loading">Загрузка…</div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="streamer-banner-root" style={cssVars as CSSProperties}>
        <div className="streamer-banner is-error">
          <strong>Твой провайдер</strong>
          <span>{error ?? 'Код не найден'}</span>
        </div>
      </div>
    );
  }

  return (
    <BannerLivePreview
      code={info.code}
      bonusDays={info.bonus_days}
      opts={opts}
      framed={false}
      pace="realtime"
    />
  );
}
